import {
  useId,
  useRef,
  useState,
  type ComponentProps,
  type DragEvent,
  type ReactNode,
} from "react";

import Text from "@/lib/components/ui/text/text";
import { cn } from "@/lib/utils";

type FileSystemEntryLike = {
  isFile: boolean;
  isDirectory: boolean;
  fullPath?: string;
  file?: (
    successCallback: (file: File) => void,
    errorCallback?: (error: DOMException) => void,
  ) => void;
  createReader?: () => {
    readEntries: (
      successCallback: (entries: FileSystemEntryLike[]) => void,
      errorCallback?: (error: DOMException) => void,
    ) => void;
  };
};

type DataTransferItemWithEntry = DataTransferItem & {
  webkitGetAsEntry?: () => FileSystemEntryLike | null;
};

export default function FileInput({
  id: providedId,
  children,
  disabled,
  className,
  onFilesChange,
  allowDirectories = false,
  ...props
}: Omit<ComponentProps<"input">, "type" | "onChange"> & {
  children?: ReactNode;
  onFilesChange?: (files: FileList | null) => void;
  allowDirectories?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const generatedId = useId();
  const id = providedId || `file-input-${generatedId}`;

  function handleDragEnter(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  }

  function handleDragLeave(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone entirely
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }

  function handleDragOver(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    e.stopPropagation();
  }

  async function handleDrop(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = allowDirectories
      ? await extractDroppedFiles(e.dataTransfer.items)
      : Array.from(e.dataTransfer.files);

    if (files.length > 0 && inputRef.current) {
      // Create a new DataTransfer object to set files on the input
      const dataTransfer = new DataTransfer();
      files.forEach((file) => {
        dataTransfer.items.add(file);
      });
      inputRef.current.files = dataTransfer.files;
      onFilesChange?.(dataTransfer.files);
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    onFilesChange?.(e.target.files);
  }

  return (
    <label
      htmlFor={id}
      data-slot="file-input"
      className={cn(
        "bg-background-neutral border-border-hovered text-foreground interactive-group has-icon has-icon-size-6 relative flex min-h-28 w-full cursor-pointer flex-col items-center justify-center gap-2.5 rounded-3xl border border-dashed p-4 text-center shadow-xs",
        isDragging && "border-border-primary",
        className,
      )}
      onDrop={handleDrop}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      aria-disabled={disabled ? true : undefined}
    >
      <input
        id={id}
        ref={inputRef}
        type="file"
        disabled={disabled}
        onChange={handleFileChange}
        className="sr-only"
        {...props}
      />
      {children}
    </label>
  );
}

export function FileInputTitle({
  className,
  ...props
}: ComponentProps<typeof Text>) {
  return (
    <Text
      size="sm"
      weight="semibold"
      variant="title"
      data-slot="file-input-title"
      className={cn("text-center", className)}
      {...props}
    />
  );
}

/**
 * extractDroppedFiles turns a drop's DataTransfer items into plain files,
 * walking dropped directories recursively and stamping each file's
 * webkitRelativePath so folder structures survive the drop.
 */
export async function extractDroppedFiles(
  items: DataTransferItemList | undefined,
): Promise<File[]> {
  if (!items || items.length === 0) return [];

  const entries = Array.from(items)
    .map(
      (item) =>
        ((item as DataTransferItemWithEntry).webkitGetAsEntry?.() ??
          null) as FileSystemEntryLike | null,
    )
    .filter((entry): entry is FileSystemEntryLike => entry !== null);

  if (entries.length === 0) {
    return Array.from(items)
      .map((item) => item.getAsFile())
      .filter((file): file is File => Boolean(file));
  }

  const files = await Promise.all(entries.map((entry) => entryToFiles(entry)));
  return files.flat();
}

async function entryToFiles(entry: FileSystemEntryLike): Promise<File[]> {
  if (entry.isFile && entry.file) {
    const file = await new Promise<File | null>((resolve) => {
      entry.file?.(
        (resolvedFile) =>
          resolve(withRelativePath(resolvedFile, entry.fullPath)),
        () => resolve(null),
      );
    });
    return file ? [file] : [];
  }

  if (!entry.isDirectory || !entry.createReader) return [];

  const reader = entry.createReader();
  const entries = await readAllEntries(reader);
  const files = await Promise.all(entries.map((child) => entryToFiles(child)));
  return files.flat();
}

async function readAllEntries(reader: {
  readEntries: (
    successCallback: (entries: FileSystemEntryLike[]) => void,
    errorCallback?: (error: DOMException) => void,
  ) => void;
}): Promise<FileSystemEntryLike[]> {
  const collected: FileSystemEntryLike[] = [];

  // WebKit directory readers return chunks until an empty array is returned.
  while (true) {
    const chunk = await new Promise<FileSystemEntryLike[]>((resolve) => {
      reader.readEntries(
        (entries) => resolve(entries ?? []),
        () => resolve([]),
      );
    });
    if (chunk.length === 0) break;
    collected.push(...chunk);
  }

  return collected;
}

function withRelativePath(file: File, fullPath?: string): File {
  if (!fullPath || !fullPath.startsWith("/")) return file;

  const relativePath = fullPath.slice(1);
  if (!relativePath) return file;

  try {
    Object.defineProperty(file, "webkitRelativePath", {
      value: relativePath,
      configurable: true,
    });
  } catch {
    return file;
  }

  return file;
}

export function FileInputDescription({
  className,
  ...props
}: ComponentProps<typeof Text>) {
  return (
    <Text
      size="sm"
      variant="label"
      data-slot="file-input-description"
      className={cn("text-foreground-pressed text-center", className)}
      {...props}
    />
  );
}
